"use client";

import { SlidersHorizontal } from "lucide-react";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { API_URL, apiFetch } from "../lib/api";
import { simulatorEventsHref } from "../lib/friendlyUrls";

export function SimulatorMenuBar() {
  const [state, setState] = useState({ loading: false, error: "", simulators: fallbackSimulators, authenticated: false });
  const [open, setOpen] = useState(false);
  const [simulatorOpen, setSimulatorOpen] = useState(false);
  const menuRef = useRef(null);
  const barRef = useRef(null);
  const pathname = usePathname();
  const [querySimulator, setQuerySimulator] = useState("");

  useEffect(() => {
    loadSimulators();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setQuerySimulator(params.get("simulador") || params.get("simulator") || "");
  }, [pathname]);

  useEffect(() => {
    function onDocumentClick(event) {
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
      if (barRef.current?.contains(event.target)) return;
      setSimulatorOpen(false);
    }

    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, []);

  async function loadSimulators() {
    setState((current) => ({ ...current, loading: true, error: "" }));

    try {
      const overview = await apiFetch("/api/v1/pilot-accounts/overview", { cache: "no-store" });
      const simulators = sortSimulatorsByName(filterCurrentStageSimulators((overview.eligibility || []).map((item) => ({
        id: item.simulator.id,
        slug: item.simulator.slug,
        name: item.simulator.name,
        shortName: item.simulator.shortName,
        logoUrl: item.simulator.logoUrl,
        logoHorizontalUrl: item.simulator.media?.logoHorizontalUrl || item.simulator.logoHorizontalUrl,
        showInMenu: item.simulator.preference?.showInMenu !== false,
      }))));
      const nextSimulators = simulators.length ? simulators : state.simulators;

      setState({
        loading: false,
        error: "",
        authenticated: true,
        simulators: nextSimulators,
      });
      return;
    } catch (error) {
      if (error.status && error.status !== 401) {
        setState((current) => ({
          ...current,
          loading: false,
          error: error.message,
          authenticated: false,
          simulators: current.simulators.length ? current.simulators : fallbackSimulators,
        }));
        return;
      }
    }

    try {
      const data = await apiFetch("/api/v1/pilot-accounts/requirements", { cache: "no-store" });
      const simulators = sortSimulatorsByName(filterCurrentStageSimulators((data.simulators || []).map((simulator) => ({
        id: simulator.id,
        slug: simulator.slug,
        name: simulator.name,
        shortName: simulator.shortName,
        logoUrl: simulator.logoUrl,
        logoHorizontalUrl: simulator.media?.logoHorizontalUrl || simulator.logoHorizontalUrl,
        showInMenu: true,
      }))));
      const nextSimulators = simulators.length ? simulators : state.simulators;

      setState({
        loading: false,
        error: "",
        authenticated: false,
        simulators: nextSimulators,
      });
    } catch (error) {
      setState((current) => ({
        ...current,
        loading: false,
        error: error.message,
        authenticated: false,
        simulators: current.simulators.length ? current.simulators : fallbackSimulators,
      }));
    }
  }

  const visibleSimulators = useMemo(() => {
    const active = state.simulators.filter((simulator) => simulator.showInMenu);
    return active.length ? active : state.simulators;
  }, [state.simulators]);
  const activeSimulator = useMemo(() => {
    return findActiveSimulator(visibleSimulators, pathname, querySimulator);
  }, [visibleSimulators, pathname, querySimulator]);

  async function toggleSimulator(simulator) {
    if (!state.authenticated) {
      window.location.href = "/perfil/cuentas";
      return;
    }

    const showInMenu = !simulator.showInMenu;
    setState((current) => ({
      ...current,
      simulators: current.simulators.map((item) => item.id === simulator.id ? { ...item, showInMenu } : item),
    }));

    try {
      await apiFetch(`/api/v1/pilot-accounts/simulators/${simulator.id}/preference`, {
        method: "PATCH",
        body: JSON.stringify({ isFollowing: showInMenu, showInMenu }),
      });
    } catch (error) {
      setState((current) => ({
        ...current,
        error: error.message,
        simulators: current.simulators.map((item) => item.id === simulator.id ? { ...item, showInMenu: simulator.showInMenu } : item),
      }));
    }
  }

  return (
    <section className={`sim-brand-bar ${simulatorOpen ? "is-open" : ""}`} aria-label="Simuladores Nitro" ref={barRef}>
      <div className="sim-brand-bar-inner">
        <button
          aria-controls="nitro-simulator-menu"
          aria-expanded={simulatorOpen}
          className="sim-brand-trigger"
          onClick={() => setSimulatorOpen((value) => !value)}
          type="button"
        >
          <span>{activeSimulator?.name || "Selecciona tu simulador"}</span>
          <strong>{activeSimulator?.shortName || "Simuladores"}</strong>
        </button>

        <nav className="sim-brand-list" id="nitro-simulator-menu" aria-label="Menu de simuladores">
          {visibleSimulators.map((simulator) => {
            const isActive = activeSimulator?.slug === simulator.slug;
            return (
              <a
                aria-current={isActive ? "page" : undefined}
                className={isActive ? "is-active" : ""}
                href={simulatorEventsHref(simulator)}
                key={simulator.id}
                onClick={() => setSimulatorOpen(false)}
                title={simulator.name}
              >
                <SimulatorLogo simulator={simulator} />
              </a>
            );
          })}
        </nav>

        {state.authenticated ? (
          <div className="sim-filter-menu" ref={menuRef}>
            <button aria-expanded={open} className="sim-filter-button" onClick={() => setOpen((value) => !value)} type="button">
              <SlidersHorizontal size={16} />
              Filtrar
            </button>
            {open ? (
              <div className="sim-filter-popover" role="menu">
                <div>
                  <strong>Simuladores en el menu</strong>
                  <span>Activa los juegos que quieres ver arriba.</span>
                </div>
                {state.simulators.map((simulator) => (
                  <label className="sim-filter-option" key={simulator.id}>
                    <input checked={!!simulator.showInMenu} onChange={() => toggleSimulator(simulator)} type="checkbox" />
                    <SimulatorLogo simulator={simulator} />
                    <span>{simulator.name}</span>
                  </label>
                ))}
                {state.error ? <p>{state.error}</p> : null}
                <a href="/perfil/cuentas">Configurar cuentas</a>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

const fallbackSimulators = [
  createFallbackSimulator(2, "assetto-corsa-pc", "Assetto Corsa (PC)", "AC"),
  createFallbackSimulator(1, "assetto-corsa-competizione-pc", "Assetto Corsa Competizione (PC)", "ACC"),
  createFallbackSimulator(11, "automobilista-2", "Automobilista 2", "AMS2"),
  createFallbackSimulator(4, "iracing", "iRacing", "iRacing"),
  createFallbackSimulator(14, "le-mans-ultimate", "Le Mans Ultimate", "LMU"),
];

const currentStageSimulatorSlugs = new Set(fallbackSimulators.map((simulator) => simulator.slug));

function filterCurrentStageSimulators(simulators) {
  return simulators.filter((simulator) => currentStageSimulatorSlugs.has(simulator.slug));
}

function createFallbackSimulator(id, slug, name, shortName) {
  return {
    id,
    slug,
    name,
    shortName,
    logoUrl: simulatorMediaUrl(slug, "vertical", 240),
    logoHorizontalUrl: simulatorMediaUrl(slug, "horizontal", 360),
    showInMenu: true,
  };
}

function simulatorMediaUrl(slug, type, width) {
  return `${API_URL}/api/v1/media/img?src=${encodeURIComponent(`/simulators/${slug}/logos/${type}.webp`)}&w=${width}&q=75&fmt=webp`;
}

function slugFromName(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function SimulatorLogo({ simulator }) {
  const logoUrl = simulator.logoHorizontalUrl || simulator.logoUrl;

  if (!logoUrl) {
    return <em aria-hidden="true">{simulator.shortName || simulator.name?.slice(0, 2) || "SN"}</em>;
  }

  return (
    <img
      alt=""
      className="sim-brand-logo"
      decoding="async"
      loading="eager"
      src={logoUrl}
    />
  );
}

function sortSimulatorsByName(simulators) {
  return [...simulators].sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "es", { sensitivity: "base" }));
}

function findActiveSimulator(simulators, pathname, querySimulatorValue) {
  const path = String(pathname || "").toLowerCase();
  const querySimulator = String(querySimulatorValue || "").toLowerCase();

  return simulators.find((simulator) => {
    const slug = String(simulator.slug || "").toLowerCase();
    if (!slug) return false;
    return querySimulator === slug
      || path.includes(`/eventos/simulador/${slug}`)
      || path.includes(`/simulador/${slug}`)
      || path.endsWith(`/eventos/${slug}`);
  }) || null;
}
